'use client';
import * as React from 'react';
import { PageScreen } from '@/page';
import { useServerAuth } from '@/auth';
import { createEventBus } from '@/shared/utils';
import { DynamicPageEventKeys, DynamicPageEvents } from './events';
import { createSubscriptionCheckoutSession } from 'src/modules/page/utils/checkout-session';
import { useFirebaseAuth } from '@/shared/hooks';
import { useRouter } from 'next/navigation';
import { sendGTMEvent } from 'src/shared/utils/gtm';
import { CheckoutSubscription } from './types';

interface PageContainerProps {
  data: any;
}

export function DynamicPageContainer({ data }: PageContainerProps) {
  const { user, hydrated } = useServerAuth();
  const auth = useFirebaseAuth();
  const router = useRouter();

  const handleCheckoutSessionSubscription = React.useCallback(
    ({ priceId, name, price, trialPeriodDays, couponName }: CheckoutSubscription) => {
      sendGTMEvent({
        event: 'begin_checkout',
        currency: 'eur',
        items: [
          {
            id: name,
            price: price,
            category: priceId,
          },
        ],
      });

      createSubscriptionCheckoutSession({
        userUid: user?.uid || '',
        successUrl: `${window.location.origin}/order-confirmation`,
        cancelUrl: window.location.href,
        priceId,
        trialPeriodDays,
        isGuest: false,
        couponName,
      });
    },
    [user, router],
  );

  const handleGuestCheckoutSessionSubscription = React.useCallback(
    ({ priceId, name, price, trialPeriodDays, email, couponName }: CheckoutSubscription) => {
      sendGTMEvent({
        event: 'begin_checkout',
        currency: 'eur',
        items: [
          {
            id: name,
            price: price,
            category: priceId,
            name: 'GUEST',
          },
        ],
      });

      createSubscriptionCheckoutSession({
        successUrl: `${process.env.NEXT_PUBLIC_HOST}/api/register/create-guest-registration`,
        cancelUrl: window.location.href,
        loginUrl: `${window.location.origin}/auth/login`,
        priceId,
        trialPeriodDays,
        isGuest: true,
        email,
        couponName,
      });
    },
    [user, router],
  );

  const eventBus = createEventBus<DynamicPageEvents>('dynamic-page');

  React.useEffect(() => {
    eventBus.on(DynamicPageEventKeys.checkout.getSubscription, handleCheckoutSessionSubscription);
    eventBus.on(
      DynamicPageEventKeys.checkout.getGuestSubscription,
      handleGuestCheckoutSessionSubscription,
    );

    return () => {
      eventBus.off(
        DynamicPageEventKeys.checkout.getSubscription,
        handleCheckoutSessionSubscription,
      );
      eventBus.off(
        DynamicPageEventKeys.checkout.getGuestSubscription,
        handleGuestCheckoutSessionSubscription,
      );
    };
  }, [eventBus]);

  if (!hydrated || !auth) return null;
  return <PageScreen key="homepage-screen" data={data} eventBus={eventBus} auth={auth} />;
}

export default DynamicPageContainer;
